<template>
  <div>
    <NavBar />
    <main class="page journees-page">
      <section class="journees-hero">
        <div>
          <span class="section-kicker">Historique terrain</span>
          <h1 class="page-title">Journées</h1>
          <p class="page-subtitle">{{ resumePeriode }}</p>
        </div>
        <button class="btn btn-secondary refresh-btn" type="button" :disabled="loading" @click="charger">
          Actualiser
        </button>
      </section>

      <div v-if="erreur" class="alert-error">
        {{ erreur }}
      </div>

      <section class="stats-grid" aria-label="Synthèse des journées">
        <div class="stat-card card">
          <span class="stat-kicker">Journées</span>
          <strong>{{ journeesFiltrees.length }}</strong>
          <small>{{ journees.length }} au total</small>
        </div>
        <div class="stat-card card">
          <span class="stat-kicker">CA total</span>
          <strong>{{ formatEuro(totalCA) }}</strong>
          <small>{{ formatEuro(moyenneCA) }} moyen</small>
        </div>
        <div class="stat-card card">
          <span class="stat-kicker">CA fidélisé</span>
          <strong>{{ formatEuro(totalCAFid) }}</strong>
          <small>{{ tauxFidelise }}% du CA</small>
        </div>
        <div class="stat-card card stat-profit" :class="{ negative: totalBenefice < 0 }">
          <span class="stat-kicker">Bénéfice</span>
          <strong>{{ formatEuro(totalBenefice) }}</strong>
          <small>{{ formatEuro(moyenneBenefice) }} moyen</small>
        </div>
      </section>

      <section class="card filters-card">
        <div class="filters-header">
          <div>
            <h2>Filtres</h2>
            <p>{{ journeesFiltrees.length }} résultat{{ journeesFiltrees.length > 1 ? 's' : '' }}</p>
          </div>
          <button class="reset-btn" type="button" @click="reinitialiserFiltres">Réinitialiser</button>
        </div>

        <div class="filters-grid">
          <label class="filter-field">
            <span class="label">Du</span>
            <input v-model="dateDebut" class="input" type="date" />
          </label>
          <label class="filter-field">
            <span class="label">Au</span>
            <input v-model="dateFin" class="input" type="date" />
          </label>
          <label class="filter-field">
            <span class="label">Marché</span>
            <select v-model="filtreVille" class="input">
              <option value="">Tous les marchés</option>
              <option v-for="ville in villesDisponibles" :key="ville" :value="ville">{{ ville }}</option>
            </select>
          </label>
          <label class="filter-field">
            <span class="label">Météo</span>
            <select v-model="filtreMeteo" class="input">
              <option value="">Toutes</option>
              <option v-for="meteo in meteosDisponibles" :key="meteo" :value="meteo">{{ meteoLabel(meteo) }}</option>
            </select>
          </label>
          <label class="filter-field">
            <span class="label">Contexte</span>
            <select v-model="filtreContexte" class="input">
              <option value="">Tous</option>
              <option v-for="contexte in contextesDisponibles" :key="contexte" :value="contexte">{{ contexteLabel(contexte) }}</option>
            </select>
          </label>
          <label class="filter-field">
            <span class="label">Tri</span>
            <select v-model="tri" class="input">
              <option value="date_desc">Date récente</option>
              <option value="date_asc">Date ancienne</option>
              <option value="ca_desc">CA le plus élevé</option>
              <option value="benefice_desc">Bénéfice le plus élevé</option>
            </select>
          </label>
        </div>

        <div class="quick-filters">
          <button type="button" :class="{ active: raccourciActif === 'today' }" @click="setIntervalle('today')">Aujourd'hui</button>
          <button type="button" :class="{ active: raccourciActif === 'week' }" @click="setIntervalle('week')">Cette semaine</button>
          <button type="button" :class="{ active: raccourciActif === 'month' }" @click="setIntervalle('month')">Ce mois</button>
          <button type="button" :class="{ active: raccourciActif === 'all' }" @click="setIntervalle('all')">Tout</button>
        </div>
      </section>

      <section v-if="filtreVille && !loading" class="market-focus">
        <div>
          <span class="section-kicker">Marché sélectionné</span>
          <h2>{{ filtreVille }}</h2>
        </div>
        <div class="focus-metrics">
          <span><strong>{{ journeesFiltrees.length }}</strong> journées</span>
          <span><strong>{{ formatEuro(moyenneCA) }}</strong> CA moyen</span>
          <span><strong>{{ formatEuro(meilleurCA) }}</strong> meilleure journée</span>
        </div>
      </section>

      <section v-if="loading" class="card state-card">
        <span class="loader"></span>
        <strong>Chargement des journées</strong>
        <p>Récupération de l'historique en cours.</p>
      </section>

      <section v-else-if="journeesFiltrees.length === 0" class="card state-card">
        <strong>Aucune journée trouvée</strong>
        <p>Modifiez les filtres ou actualisez l'historique.</p>
      </section>

      <section v-else class="journees-list" aria-label="Liste des journées">
        <article class="journee-card card" v-for="j in journeesFiltrees" :key="j.journee_id || `${j.date}-${j.ville}`">
          <div class="journee-main">
            <div class="journee-date">
              <span class="date-day">{{ formatDate(j.date) }}</span>
              <span class="date-market" v-if="j.ville">{{ j.ville }}</span>
            </div>
            <div class="journee-actions">
              <span class="status-badge" :class="j.statut === 'cloture' ? 'closed' : 'open'">
                {{ j.statut === 'cloture' ? 'Clôturé' : 'En cours' }}
              </span>
              <button class="delete-btn" type="button" @click="supprimerJournee(j)" title="Supprimer cette journée" aria-label="Supprimer cette journée">
                ×
              </button>
            </div>
          </div>

          <div class="journee-tags" v-if="j.meteo || j.contexte">
            <span v-if="j.meteo">{{ meteoLabel(j.meteo) }}</span>
            <span v-if="j.contexte">{{ contexteLabel(j.contexte) }}</span>
          </div>

          <div class="metrics-grid">
            <div class="metric">
              <span>CA total</span>
              <strong>{{ formatEuro(caJournee(j)) }}</strong>
              <small>{{ j.nb_passages_carte || 0 }} passages carte</small>
            </div>
            <div class="metric">
              <span>Fidélisé</span>
              <strong>{{ formatEuro(j.ca_fidelise) }}</strong>
              <small>{{ tauxFideliseJournee(j) }}% du CA</small>
            </div>
            <div class="metric">
              <span>Sans carte</span>
              <strong>{{ formatEuro(j.ca_sans_fidelite) }}</strong>
              <small>Ventes directes</small>
            </div>
            <div class="metric">
              <span>Dépenses</span>
              <strong>{{ formatEuro(j.total_depenses) }}</strong>
              <small>Hors fond de caisse</small>
            </div>
            <div class="metric profit" :class="{ negative: nombre(j.marge_brute) < 0 }">
              <span>Bénéfice</span>
              <strong>{{ j.marge_brute ? formatEuro(j.marge_brute) : '—' }}</strong>
              <small>{{ j.marge_pct ? j.marge_pct + '%' : 'Après clôture' }}</small>
            </div>
          </div>

          <p class="journee-commentaire" v-if="j.commentaire">{{ j.commentaire }}</p>
        </article>
      </section>
    </main>
  </div>
</template>

<script>
import NavBar from '../components/NavBar.vue'
import api from '../api'

export default {
  name: 'JourneesView',
  components: { NavBar },
  data() {
    const aujourdhui = this.dateISO(new Date())
    return {
      journees: [],
      filtreVille: '',
      filtreMeteo: '',
      filtreContexte: '',
      tri: 'date_desc',
      loading: true,
      erreur: '',
      dateDebut: aujourdhui,
      dateFin: aujourdhui,
      raccourciActif: 'today'
    }
  },
  computed: {
    journeesFiltrees() {
      const list = this.journees.filter(j => {
        const date = this.dateJournee(j)
        if (this.dateDebut && date < this.dateDebut) return false
        if (this.dateFin && date > this.dateFin) return false
        if (this.filtreVille && j.ville !== this.filtreVille) return false
        if (this.filtreMeteo && j.meteo !== this.filtreMeteo) return false
        if (this.filtreContexte && j.contexte !== this.filtreContexte) return false
        return true
      })

      return list.sort((a, b) => {
        if (this.tri === 'date_asc') return this.dateJournee(a).localeCompare(this.dateJournee(b))
        if (this.tri === 'ca_desc') return this.caJournee(b) - this.caJournee(a)
        if (this.tri === 'benefice_desc') return this.nombre(b.marge_brute) - this.nombre(a.marge_brute)
        return this.dateJournee(b).localeCompare(this.dateJournee(a))
      })
    },
    villesDisponibles() {
      return this.valeursDisponibles('ville')
    },
    meteosDisponibles() {
      return this.valeursDisponibles('meteo')
    },
    contextesDisponibles() {
      return this.valeursDisponibles('contexte')
    },
    totalCA() {
      return this.journeesFiltrees.reduce((s, j) => s + this.caJournee(j), 0)
    },
    totalCAFid() {
      return this.journeesFiltrees.reduce((s, j) => s + this.nombre(j.ca_fidelise), 0)
    },
    totalBenefice() {
      return this.journeesFiltrees.reduce((s, j) => s + this.nombre(j.marge_brute), 0)
    },
    moyenneCA() {
      return this.journeesFiltrees.length ? this.totalCA / this.journeesFiltrees.length : 0
    },
    moyenneBenefice() {
      return this.journeesFiltrees.length ? this.totalBenefice / this.journeesFiltrees.length : 0
    },
    meilleurCA() {
      return this.journeesFiltrees.length ? Math.max(...this.journeesFiltrees.map(this.caJournee)) : 0
    },
    tauxFidelise() {
      if (!this.totalCA) return 0
      return Math.round((this.totalCAFid / this.totalCA) * 100)
    },
    resumePeriode() {
      if (!this.dateDebut && !this.dateFin) return 'Toutes les journées disponibles'
      if (this.dateDebut === this.dateFin) return `Journée du ${this.formatDateCourte(this.dateDebut)}`
      if (this.dateDebut && this.dateFin) return `Du ${this.formatDateCourte(this.dateDebut)} au ${this.formatDateCourte(this.dateFin)}`
      if (this.dateDebut) return `Depuis le ${this.formatDateCourte(this.dateDebut)}`
      return `Jusqu'au ${this.formatDateCourte(this.dateFin)}`
    }
  },
  watch: {
    dateDebut() { this.raccourciActif = '' },
    dateFin() { this.raccourciActif = '' }
  },
  methods: {
    nombre(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    },
    dateISO(d) {
      const date = new Date(d)
      date.setHours(12, 0, 0, 0)
      return date.toISOString().split('T')[0]
    },
    dateJournee(j) {
      return j?.date ? String(j.date).substring(0, 10) : ''
    },
    caJournee(j) {
      return this.nombre(j.ca_total) || this.nombre(j.ca_fidelise) + this.nombre(j.ca_sans_fidelite)
    },
    valeursDisponibles(champ) {
      return [...new Set(this.journees.map(j => j[champ]).filter(Boolean))]
        .sort((a, b) => String(a).localeCompare(String(b), 'fr-FR'))
    },
    formatEuro(v) {
      return this.nombre(v).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
    },
    formatDate(d) {
      if (!d) return 'Date inconnue'
      return new Date(d).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    },
    formatDateCourte(d) {
      if (!d) return ''
      return new Date(`${d}T12:00:00`).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    },
    meteoLabel(m) {
      return { soleil: 'Soleil', nuageux: 'Nuageux', pluie: 'Pluie', vent: 'Vent', froid: 'Froid' }[m] || m
    },
    contexteLabel(c) {
      return { normal: 'Normal', vacances: 'Vacances', ferie: 'Férié', evenement: 'Événement' }[c] || c
    },
    tauxFideliseJournee(j) {
      const total = this.caJournee(j)
      if (!total) return 0
      return Math.round((this.nombre(j.ca_fidelise) / total) * 100)
    },
    setIntervalle(type) {
      const today = new Date()
      const end = this.dateISO(today)

      if (type === 'today') {
        this.dateDebut = end
        this.dateFin = end
      } else if (type === 'week') {
        const monday = new Date(today)
        const day = monday.getDay() || 7
        monday.setDate(monday.getDate() - day + 1)
        this.dateDebut = this.dateISO(monday)
        this.dateFin = end
      } else if (type === 'month') {
        this.dateDebut = this.dateISO(new Date(today.getFullYear(), today.getMonth(), 1))
        this.dateFin = end
      } else {
        this.dateDebut = ''
        this.dateFin = ''
      }

      this.raccourciActif = type
    },
    reinitialiserFiltres() {
      this.filtreVille = ''
      this.filtreMeteo = ''
      this.filtreContexte = ''
      this.tri = 'date_desc'
      this.setIntervalle('today')
    },
    async supprimerJournee(j) {
      const message = j.statut === 'cloture'
        ? 'Cette journée est clôturée. Supprimer quand même ? Toutes les transactions associées seront perdues.'
        : 'Supprimer cette journée ? Les transactions associées seront perdues.'
      if (!confirm(message)) return

      try {
        await api.delete('/journee/' + j.journee_id)
        await this.charger()
      } catch (err) {
        alert('Erreur lors de la suppression : ' + (err.response?.data?.error || err.message))
      }
    },
    async charger() {
      this.loading = true
      this.erreur = ''
      try {
        const res = await api.get('/journee/historique')
        this.journees = Array.isArray(res.data) ? res.data : []
      } catch (err) {
        this.erreur = "Impossible de charger l'historique des journées."
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    this.charger()
  }
}
</script>

<style scoped>
.journees-page {
  max-width: 1180px;
}

.journees-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.section-kicker,
.stat-kicker {
  display: block;
  color: var(--fm-accent);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.page-title {
  margin: 4px 0 8px;
  font-size: 34px;
}

.page-subtitle {
  max-width: 620px;
  font-size: 15px;
}

.refresh-btn {
  width: auto;
  min-width: 132px;
}

.alert-error {
  margin-bottom: 16px;
  padding: 14px 16px;
  border: 1px solid #fecaca;
  border-radius: 14px;
  background: var(--fm-danger-soft);
  color: #991b1b;
  font-weight: 700;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.stat-card {
  min-height: 132px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.stat-card strong {
  margin-top: 10px;
  color: var(--fm-text);
  font-size: 24px;
  line-height: 1.15;
}

.stat-card small {
  color: var(--fm-muted);
  font-size: 13px;
  font-weight: 600;
}

.stat-profit {
  border-color: #bbf7d0;
  background: linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%);
}

.stat-profit strong {
  color: #15803d;
}

.stat-profit.negative {
  border-color: #fecaca;
  background: linear-gradient(180deg, #ffffff 0%, #fef2f2 100%);
}

.stat-profit.negative strong {
  color: #dc2626;
}

.filters-card {
  margin-bottom: 16px;
}

.filters-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.filters-header h2,
.market-focus h2 {
  font-size: 18px;
}

.filters-header p {
  margin-top: 2px;
  font-size: 13px;
}

.reset-btn {
  padding: 9px 12px;
  border: 1px solid var(--fm-border);
  border-radius: 10px;
  background: var(--fm-surface);
  color: var(--fm-navy);
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.reset-btn:hover {
  background: var(--fm-surface-soft);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}

.filter-field {
  min-width: 0;
}

.quick-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.quick-filters button {
  padding: 9px 14px;
  border: 1px solid var(--fm-border);
  border-radius: 999px;
  background: var(--fm-surface);
  color: var(--fm-muted);
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.quick-filters button.active,
.quick-filters button:hover {
  border-color: #bfdbfe;
  background: var(--fm-accent-soft);
  color: #1d4ed8;
}

.market-focus {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  padding: 18px 20px;
  border: 1px solid #bfdbfe;
  border-radius: var(--fm-card-radius);
  background: #eff6ff;
}

.focus-metrics {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.focus-metrics span {
  padding: 8px 12px;
  border-radius: 999px;
  background: white;
  color: var(--fm-muted);
  font-size: 13px;
  font-weight: 700;
}

.focus-metrics strong {
  color: var(--fm-text);
}

.state-card {
  display: flex;
  min-height: 190px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.state-card strong {
  margin-top: 12px;
  font-size: 18px;
}

.loader {
  width: 34px;
  height: 34px;
  border: 3px solid var(--fm-border);
  border-top-color: var(--fm-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.journees-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.journee-card {
  margin-bottom: 0;
}

.journee-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--fm-border);
}

.journee-date {
  min-width: 0;
}

.date-day {
  display: block;
  color: var(--fm-text);
  font-size: 17px;
  font-weight: 800;
  text-transform: capitalize;
}

.date-market {
  display: inline-flex;
  margin-top: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  background: var(--fm-accent-soft);
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 800;
}

.journee-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.status-badge {
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.status-badge.closed {
  background: var(--fm-success-soft);
  color: #166534;
}

.status-badge.open {
  background: var(--fm-warning-soft);
  color: #92400e;
}

.delete-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #fecaca;
  border-radius: 10px;
  background: white;
  color: #dc2626;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.delete-btn:hover {
  background: var(--fm-danger-soft);
}

.journee-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.journee-tags span {
  padding: 5px 10px;
  border-radius: 999px;
  background: var(--fm-surface-soft);
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 800;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.metric {
  min-height: 98px;
  padding: 12px;
  border: 1px solid var(--fm-border);
  border-radius: 14px;
  background: #f8fafc;
}

.metric span,
.metric small {
  display: block;
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 700;
}

.metric strong {
  display: block;
  margin: 6px 0 3px;
  color: var(--fm-text);
  font-size: 17px;
  line-height: 1.2;
}

.metric.profit {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.metric.profit strong {
  color: #15803d;
}

.metric.profit.negative {
  border-color: #fecaca;
  background: #fef2f2;
}

.metric.profit.negative strong {
  color: #dc2626;
}

.journee-commentaire {
  margin-top: 14px;
  padding: 12px 14px;
  border-left: 3px solid var(--fm-warning);
  border-radius: 10px;
  background: #fffbeb;
  color: #78350f;
  font-size: 13px;
  font-weight: 600;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 1040px) {
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filters-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .metrics-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 680px) {
  .journees-page {
    padding: 18px 12px 96px;
  }

  .journees-hero,
  .filters-header,
  .market-focus,
  .journee-main {
    flex-direction: column;
    align-items: stretch;
  }

  .page-title {
    font-size: 28px;
  }

  .refresh-btn,
  .reset-btn {
    width: 100%;
  }

  .stats-grid,
  .filters-grid,
  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .focus-metrics {
    justify-content: flex-start;
  }

  .journee-actions {
    justify-content: space-between;
  }
}
</style>
